import { storage } from '@/utils/storage';

interface JwtPayload {
  exp?: number;
  scope?: string;
  sub?: string;
  userId?: number | string;
  token_type?: 'access' | 'refresh';
}

const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const encodedPayload = token.split('.')[1];
    if (!encodedPayload) return null;
    const normalizedPayload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - normalizedPayload.length % 4) % 4),
      '=',
    );
    return JSON.parse(decodeURIComponent(
      Array.from(atob(paddedPayload))
        .map((character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    )) as JwtPayload;
  } catch {
    return null;
  }
};

export const getTokenExpiryTime = (token: string): string | null => {
  const expiry = decodeJwtPayload(token)?.exp;
  return expiry ? new Date(expiry * 1000).toISOString() : null;
};

export const getAuthenticatedUsername = (token: string | null): string | null => {
  if (!token) return null;
  const subject = decodeJwtPayload(token)?.sub;
  return typeof subject === 'string' && subject.trim() ? subject.trim() : null;
};

const isTokenUsableForType = (
  token: string | null,
  expectedType: JwtPayload['token_type'],
): boolean => {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  return payload !== null
    && payload.token_type === expectedType
    && typeof payload.exp === 'number'
    && payload.exp * 1000 > Date.now();
};

export const isTokenUsable = (token: string | null): boolean =>
  isTokenUsableForType(token, 'access');

export const isRefreshTokenUsable = (token: string | null): boolean =>
  isTokenUsableForType(token, 'refresh');

export const tokenHasPermission = (token: string | null, permission: string): boolean => {
  if (!token || !isTokenUsable(token)) return false;
  const authorities = decodeJwtPayload(token)?.scope?.split(/\s+/).filter(Boolean) ?? [];
  return authorities.includes(permission);
};

export const hasStoredPermission = (permission: string): boolean =>
  tokenHasPermission(storage.getAccessToken(), permission);
